import clsx from "clsx";
import { createStyles, makeStyles } from "@mui/styles";
import {
    Box,
    BoxProps,
    InternalStandardProps,
    List,
    ListItem,
    Theme,
} from "@mui/material";
import { Layout } from "../Layout";
import { AppTypography } from "JS/React/Components/AppTypography";
import { useHistory } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { clearLocalStorage, isBetaEnvironment } from "JS/Helpers";
import { config } from "JS/Config";
import { useResetGlobalState } from "JS/React/Hooks/Media";
import localforage from "localforage";
import { useEffect, useMemo, useState } from "react";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import {
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
} from "JS/Models";
import { useSnackbar } from "notistack";
import { AppDivider } from "JS/React/Components/AppDivider";
import {
    useDeleteDeviceToken,
    usePermissionHelpers,
} from "JS/React/Hooks/Notification";
import { messaging } from "JS/Helpers/UserMessaging";
import { getLocalFcmToken, getWebcastUUID } from "JS/Helpers/PushNotifications";
import { useTeamInformation } from "JS/React/Hooks/Home";
import { useResumeData } from "JS/React/Hooks/Firebase/Resume";
import { useAppSelector } from "JS/Redux/Store";
import { UserRoute } from "../../../Helpers/FycStatusConstants";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useJwtToken } from "JS/React/Hooks/Users";
import { optOutEndpoint } from "JS/Services/pinPointService/pinpointEndpointService";

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {},
        grey600: {
            color: theme.palette.grey[600],
        },
        listItem: {
            fontWeight: 400,
            display: "flex",
            justifyContent: "flex-start",
        },
        listText: { fontWeight: 400 },
        list: {
            flexGrow: 1,
        },
        listIcon: {
            fontWeight: 400,
            fontSize: "24px",
            marginRight: "5%",
            width: "10%",
        },
        versionText: {
            color: theme.palette.common.black,
        },
        fontSize: {
            fontSize: "28px",
        },
    }),
);

export interface MoreMenuProps extends InternalStandardProps<BoxProps> {}

export const clearLocalforageKeepHistory = async () => {
    const PERSIST_KEY = "persist:REACT_APP_WEB_MEDIA";
    const raw = await localforage.getItem(PERSIST_KEY);
    if (!raw) return;

    const parsed = JSON.parse(raw as string);
    const userObj = parsed?.userHistory ? JSON.parse(parsed?.userHistory) : {};
    const uniqueIds = userObj?.login_details?.reduce((acc, curr) => {
        if (!acc.some((item) => item.member_id === curr.member_id)) {
            acc.push(curr);
        }
        return acc;
    }, []);

    const preserved = {
        userHistory: JSON.stringify({
            login_details: uniqueIds,
        }),
        _persist: parsed._persist ?? { version: -1, rehydrated: true },
    };

    await localforage.setItem(PERSIST_KEY, JSON.stringify(preserved));
};

export const MoreMenu = (props: MoreMenuProps) => {
    const classes = useStyles(props);
    const { className, ...rest } = props;

    const history = useHistory();
    const { linkProvider } = useRouting();
    const { resetReduxState } = useResetGlobalState();
    const { enqueueSnackbar } = useSnackbar();

    const [isOpenConfirmDailog, setIsOpenConfirmDailog] = useState(false);
    const [logoutLoader, setLogoutLoader] = useState(false);

    const { logFirebaseEvent } = useFirebaseLogger();

    const { deleteDeviceToken } = useDeleteDeviceToken();
    const { disableJwtToken } = useJwtToken();
    const { initializeNotifications } = usePermissionHelpers();

    const {
        logAudioResume,
        logVideoResume,
        logPlaylistResume,
        logAudioBundleResume,
    } = useResumeData();

    const { refetch, loading: loadingTeamInfo } = useTeamInformation(true);

    const userTeamInformation = useAppSelector(
        (state) => state.home.userTeamInformation,
    );

    useEffect(() => {
        refetch();
    }, []);

    const isApprenticeSubcriber = useMemo(
        () =>
            !!userTeamInformation?.activeSubscriptions?.includes("Apprentice"),
        [userTeamInformation],
    );

    const logout = async () => {
        const disableJwt = async () => {
            try {
                await disableJwtToken();
            } catch (err) {
                enqueueSnackbar(messaging.logout.failed, {
                    variant: "error",
                });
                initializeNotifications();
                throw err;
            }
        };

        const token = getLocalFcmToken();
        const deleteToken = async () => {
            try {
                await deleteDeviceToken(config.user.memberId, token);
                const deviceId = getWebcastUUID();
                await optOutEndpoint(config.user.memberId, deviceId);
            } catch (err) {
                enqueueSnackbar(messaging.logout.failed, {
                    variant: "error",
                });
                throw err;
            }
        };
        try {
            setLogoutLoader(true);
            if (!!token) await deleteToken();
            await disableJwt();

            try {
                await logAudioResume();
                await logVideoResume();
                await logPlaylistResume();
                await logAudioBundleResume();
            } catch (error) {
                throw error;
            }
            resetReduxState();
            clearLocalStorage();
            await clearLocalforageKeepHistory();

            const action: FirebaseEventAction = {
                action: EventActions.LOGOUT,
                category: EventCategories.GENERAL,
            };

            logFirebaseEvent(EventNames.LOGGED_OUT, action);
            setTimeout(() => setLogoutLoader(false), 1000);
            window.location.reload();
        } catch (err) {
            setLogoutLoader(false);
        }
    };
    const handleToggleConfirmDialog = () => {
        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const downloadQueue = localStorage.getItem("downloadQueue")
        ? JSON.parse(localStorage.getItem("downloadQueue"))
        : [];

    const handleNavigateToQueue = () =>
        history.push(linkProvider.react.downloadingQueue().index());

    return (
        <Layout>
            <Box
                {...rest}
                className={clsx(className, classes.root)}
                display={"flex"}
                flexDirection={"column"}
                height={"100%"}
            >
                <List className={classes.list}>
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.gifts().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "gift"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Gifts
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(
                                linkProvider.react.giftDashboard().index(),
                            )
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "dashboard"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Gift Dashboard
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.documents().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["far", "clipboard"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Documents
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.favorites().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "star"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Favorites
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.events().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["far", "address-card"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            My Event Registrations
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    {isApprenticeSubcriber && (
                        <>
                            <ListItem
                                button
                                onClick={() =>
                                    history.push(
                                        linkProvider.react
                                            .fycStatus()
                                            .index(UserRoute.PRIMARY_USER),
                                    )
                                }
                                className={clsx(classes.listItem)}
                            >
                                <FontAwesomeIcon
                                    className={clsx(
                                        classes.grey600,
                                        classes.listIcon,
                                        classes.fontSize,
                                    )}
                                    icon={["far", "id-badge"]}
                                />
                                <AppTypography
                                    className={clsx(
                                        classes.grey600,
                                        classes.listText,
                                    )}
                                    variant="h6"
                                >
                                    My FCY Status
                                </AppTypography>
                            </ListItem>
                            <AppDivider />
                        </>
                    )}
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.playlist().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "music"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Audio Playlists
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.webcast().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "earth-americas"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Webcast
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.appInfo().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["far", "file-lines"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            App Info
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={handleNavigateToQueue}
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "file-arrow-down"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Downloading Queue
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(linkProvider.react.feedback().index())
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "message"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Feedback
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={() =>
                            history.push(
                                linkProvider.react.appSettings().index(),
                            )
                        }
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "cogs"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Settings
                        </AppTypography>
                    </ListItem>
                    <AppDivider />
                    <ListItem
                        button
                        onClick={handleToggleConfirmDialog}
                        className={clsx(classes.listItem)}
                    >
                        <FontAwesomeIcon
                            className={clsx(classes.grey600, classes.listIcon)}
                            icon={["fas", "power-off"]}
                        />
                        <AppTypography
                            className={clsx(classes.grey600, classes.listText)}
                            variant="h6"
                        >
                            Logout
                        </AppTypography>
                    </ListItem>

                    <AppDivider />
                </List>
                <AppTypography
                    textAlign={"right"}
                    variant="caption"
                    className={classes.versionText}
                >
                    Version:{" "}
                    {isBetaEnvironment()
                        ? config.betaAppVersion
                        : config.appVersion}
                </AppTypography>
                {isOpenConfirmDailog && (
                    <AppConfimationDialog
                        open={isOpenConfirmDailog}
                        onConfirm={logout}
                        btnConfirmLoader={logoutLoader}
                        onClose={handleToggleConfirmDialog}
                        popupMainText={
                            downloadQueue?.length > 0
                                ? `Downloading is in-progress in Queue. Downloading Queue data will be lost. Are you sure you want to log out?`
                                : "Are you sure you want to logout?"
                        }
                        confirmText="Logout"
                        cancelButtonText="Cancel"
                        showConfirmFirst={true}
                    />
                )}
                {loadingTeamInfo && (
                    <AppBackdropProgress
                        open={loadingTeamInfo}
                        backdropText={messaging.loader.loadingSession}
                    />
                )}
            </Box>
        </Layout>
    );
};
